import * as THREE from 'three';
import { forgeMaterial } from './primitives.js';

/**
 * The shared material palette for every weapon model.
 *
 * Pulled out of `index.js` when the roster went from six weapons to twenty-three:
 * one `materials(ctx)` call per build meant twenty-three copies of the lens and bore
 * materials, and every copy is a separate shader program the warmup pass has to
 * compile. They are cached per-context here instead, so all twenty-three weapons
 * share one program per surface and `renderer.compile` sees six materials rather
 * than a hundred and thirty.
 *
 * Reading order for anyone touching the optics: `lens` and `bore` carry the two
 * hard-won fixes from the first pass and the comments on them are load-bearing.
 */

const _cache = new WeakMap();

function hdrBasic(r, g, b, opacity = 1) {
  const m = new THREE.MeshBasicMaterial({ toneMapped: false, transparent: true, opacity });
  m.color.setRGB(r, g, b);
  return m;
}

export function materials(ctx) {
  const key = ctx ?? globalThis;
  if (_cache.has(key)) return _cache.get(key);

  const M = {
    /** Parkerised steel. Carries curvature-driven edge wear, which is why every box
     *  in this library goes through `chamferBox`: the bevel is what the wear lives on. */
    metal: forgeMaterial(ctx, 'gunmetal'),
    /** Brighter machined steel for bolts, charging handles and pins. */
    dark: forgeMaterial(ctx, 'gunmetal', { color: 0x8a8f95, roughness: 0.62 }),
    /** Blued/blacked steel — barrels, suppressor tubes, shrouds. */
    blued: forgeMaterial(ctx, 'gunmetal', { color: 0x4a4d52, roughness: 0.34 }),
    /** Black weapon-furniture polymer. */
    poly: forgeMaterial(ctx, 'polymer'),
    /** FDE / desert-tan furniture. */
    tan: forgeMaterial(ctx, 'polymer', { color: 0x9a8763 }),
    /** Grey-green polymer, for the weapons that need a third furniture tone so two
     *  guns of the same class do not read as recolours of each other. */
    grey: forgeMaterial(ctx, 'polymer', { map: null, color: 0x4c5157, roughness: 0.72 }),
    /**
     * Wood-look furniture.
     *
     * The forge's polymer albedo is a near-black weapon tone (~0.06 linear), and a
     * colour multiplier can only ever darken — the same trap the gloves hit. Dropping
     * the map and driving colour directly is what lets a stock read as walnut rather
     * than as a slightly warm black brick.
     */
    wood: forgeMaterial(ctx, 'polymer', { map: null, color: 0x6d4526, roughness: 0.52 }),
    /** Lighter, redder wood for the retro SMG and the break-action, so the three
     *  wood-furniture weapons in the roster are not interchangeable. */
    woodLight: forgeMaterial(ctx, 'polymer', { map: null, color: 0x8a5a2e, roughness: 0.46 }),
    /** Recoil pads, grip overmould, carry-handle sleeves. */
    rubber: forgeMaterial(ctx, 'polymer', { map: null, color: 0x17181a, roughness: 0.96 }),
    /** Loaded brass, for belts and the shotgun's shell props. */
    brass: forgeMaterial(ctx, 'gunmetal', { map: null, color: 0xb08a3c, roughness: 0.40 }),
    /** Emissive accent for the three "energy" weapons — coil windings and status
     *  strips. Basic + HDR so the bloom pass picks it up. */
    accent: hdrBasic(0.25, 1.5, 2.4, 1),

    /**
     * Optic glass.
     *
     * This was an *opaque* near-mirror metal, so aiming down any sight showed a black
     * disc. Glass is a dielectric: metalness 0, and it has to actually let the world
     * through.
     *
     * `depthWrite: false` matters as much as the opacity. The viewmodel scene is
     * composited over the world with depth cleared but colour preserved, so a
     * transparent lens blends against the already-rendered world — but only if it does
     * not write depth and occlude the far element behind it.
     *
     * `color` is the diffuse albedo, and glass has essentially none. At near-white the
     * lens was a sheet of lit paper: a world pixel of (100, 71, 60) came out
     * (144, 132, 122), washed 40% brighter and desaturated, which is why the sight
     * picture read as a flat grey plate. The anti-reflective tint has to live in the
     * *specular* — which is what an AR coating physically is — while the body of the
     * glass stays dark so the world passes through unchanged.
     */
    lens: new THREE.MeshPhysicalMaterial({
      color: 0x0b1417,
      transparent: true,
      opacity: 0.10,
      roughness: 0.05,
      metalness: 0,
      depthWrite: false,
      envMapIntensity: 0.55,
      specularIntensity: 0.8,
      specularColor: 0x9fd8e8,
      transmission: 0,
      side: THREE.DoubleSide,
    }),
    /** Warmer coating for the thermal/holographic units, so a holo window does not
     *  look like a red dot with a square hood. */
    lensAmber: new THREE.MeshPhysicalMaterial({
      color: 0x140f06,
      transparent: true,
      opacity: 0.12,
      roughness: 0.05,
      metalness: 0,
      depthWrite: false,
      envMapIntensity: 0.5,
      specularIntensity: 0.85,
      specularColor: 0xe8c07a,
      transmission: 0,
      side: THREE.DoubleSide,
    }),

    /**
     * Optic tube interior. Anodised matte black, and deliberately NOT the shared
     * gunmetal: gunmetal carries bright bare-steel edge wear, which turns the inside
     * of a sight into a lit tunnel the moment the glass becomes see-through. A real
     * optic's bore is flat black precisely so the shooter's eye ignores it.
     *
     * `envMapIntensity` is near zero because the sky is a sunset and even a little of
     * it on a tube wall reads as a lit tunnel.
     */
    bore: new THREE.MeshStandardMaterial({
      color: 0x050507,
      roughness: 1.0,
      metalness: 0,
      envMapIntensity: 0.03,
      side: THREE.BackSide,
    }),

    // Reticles are emissive light sources, not painted dots — pushed into HDR so the
    // bloom pass picks them up. A 1.0 red dot next to a sunlit receiver reads as a
    // sticker; a 6.0 one reads as an illuminated sight.
    dot: hdrBasic(3.1, 0.30, 0.12, 0.96),
    dotGreen: hdrBasic(0.35, 3.0, 0.55, 0.96),
    glow: hdrBasic(0.5, 2.9, 1.3, 0.9),
    /** Etched (non-illuminated) reticle ink — a scope's glass reticle is black. */
    ink: new THREE.MeshBasicMaterial({ color: 0x08080a, toneMapped: false }),
  };

  _cache.set(key, M);
  return M;
}

/**
 * Radial alpha ramp: transparent in the middle, opaque at the rim.
 *
 * This is the scope shadow — the ring of black that creeps in from the edge of a
 * magnified optic's ocular. It is the single strongest cue that a sight magnifies,
 * because it is the one thing a red dot never has. Generated rather than downloaded,
 * like everything else in this project.
 *
 * `inner` is set so the ramp begins just *outside* the tube's own clear cone: any
 * tighter and the shadow eats the sight picture rather than softening the edge of it.
 */
let _vignetteTex = null;
export function vignetteTexture(size = 64, inner = 0.58, outer = 0.99) {
  if (_vignetteTex) return _vignetteTex;
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const r = Math.hypot((x - c) / c, (y - c) / c);
      let a = (r - inner) / Math.max(1e-4, outer - inner);
      a = a <= 0 ? 0 : a >= 1 ? 1 : a * a * (3 - 2 * a);
      const i = (y * size + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 0;
      data[i + 3] = Math.round(a * 255);
    }
  }
  const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  _vignetteTex = t;
  return t;
}
